import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ShieldX, Award, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export default function CredentialVerify() {
  const { cid } = useParams();
  const [search] = useSearchParams();
  const sig = search.get("sig");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const venue = search.get("venue");
    const gate = search.get("gate");
    // If ?venue is present, this is a real venue-side scan → log it via POST /scan
    // Otherwise just verify (idempotent read via GET /verify).
    if (venue) {
      const qs = new URLSearchParams();
      if (sig) qs.set("sig", sig);
      qs.set("venue", venue);
      if (gate) qs.set("gate", gate);
      api.post(`/festival/scan/${cid}?${qs.toString()}`)
        .then((r) => setData(r.data))
        .catch(() => setData({ status: "error", message: "Scan service unreachable." }))
        .finally(() => setLoading(false));
    } else {
      api.get(`/festival/verify/${cid}${sig ? `?sig=${encodeURIComponent(sig)}` : ""}`)
        .then((r) => setData(r.data))
        .catch(() => setData({ status: "error", message: "Verification service unreachable." }))
        .finally(() => setLoading(false));
    }
  }, [cid, sig, search]);

  if (loading) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p>Verifying credential…</p>
        </div>
      </Shell>
    );
  }

  const s = data?.status || "unknown";
  const palette = {
    active: { Icon: ShieldCheck, color: "text-emerald-400", border: "border-emerald-500/40", bg: "bg-emerald-500/10", label: "VALID CREDENTIAL", chip: "bg-emerald-500/20 text-emerald-300" },
    revoked: { Icon: ShieldX, color: "text-red-400", border: "border-red-500/40", bg: "bg-red-500/10", label: "REVOKED", chip: "bg-red-500/20 text-red-300" },
    tampered: { Icon: ShieldAlert, color: "text-orange-400", border: "border-orange-500/40", bg: "bg-orange-500/10", label: "TAMPERED / FORGED", chip: "bg-orange-500/20 text-orange-300" },
    unknown: { Icon: ShieldAlert, color: "text-gray-400", border: "border-white/10", bg: "bg-white/[0.02]", label: "UNKNOWN", chip: "bg-white/10 text-gray-300" },
    error: { Icon: ShieldAlert, color: "text-gray-400", border: "border-white/10", bg: "bg-white/[0.02]", label: "SERVICE ERROR", chip: "bg-white/10 text-gray-300" },
  }[s] || { Icon: ShieldAlert, color: "text-gray-400", border: "border-white/10", bg: "bg-white/[0.02]", label: s.toUpperCase(), chip: "bg-white/10 text-gray-300" };
  const Icon = palette.Icon;

  return (
    <Shell>
      <div data-testid={`verify-status-${s}`} className={`w-full max-w-lg rounded-2xl border ${palette.border} ${palette.bg} p-8 backdrop-blur-md`}>
        <div className="flex flex-col items-center text-center">
          <Icon className={`w-16 h-16 ${palette.color} mb-3`} />
          <span className={`text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1 rounded-full ${palette.chip}`}>{palette.label}</span>

          {data?.name && (
            <>
              <h1 data-testid="verify-name" className="text-3xl md:text-4xl font-black mt-6 tracking-tight">{data.name}</h1>
              <p className="text-amber-300 uppercase tracking-[0.18em] text-sm font-bold mt-2">{data.role}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-xs text-gray-400">
                <Award className="w-3.5 h-3.5 text-amber-300" />
                World Film Festival · Edition {data.year}
              </div>
            </>
          )}

          {data?.message && (
            <p className="mt-5 text-sm text-gray-400 max-w-sm">{data.message}</p>
          )}

          <div className="mt-7 pt-5 border-t border-white/5 w-full text-xs text-gray-500 grid grid-cols-2 gap-3 text-left">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-600 mb-0.5">Credential ID</div>
              <div className="font-mono text-gray-300">{data?.credential_id || cid}</div>
            </div>
            {data?.issued_at_display && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-600 mb-0.5">Issued</div>
                <div className="text-gray-300">{data.issued_at_display}</div>
              </div>
            )}
            {s === "revoked" && data?.revoked_at && (
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-red-300 mb-0.5">Revoked on</div>
                <div className="text-red-300">{new Date(data.revoked_at).toLocaleString()}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-600 mt-6 text-center max-w-md">
        This page proves authenticity of a printed World Film Festival credential. Scan the QR on the card with any phone camera to land here.
      </p>
      <Link to="/" className="mt-3 text-xs text-gray-500 hover:text-amber-300 transition">← Back to CineCinema</Link>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0A0A] via-[#150608] to-[#0A0A0A] text-white flex flex-col items-center justify-center px-6 py-12">
      <div className="mb-8 text-center">
        <span className="text-[#E50914] font-black text-2xl tracking-tight">CINECINEMA</span>
        <div className="text-[10px] tracking-[0.3em] text-amber-300 uppercase mt-1">World Film Festival · Verification</div>
      </div>
      {children}
    </div>
  );
}
